import { QUALITY_TIERS, QUALITY_TIER_NAMES, UNCERTAINTY_BY_TIER, QUALITY_LEVELS } from '../constants/importConstants';

/**
 * Maps API string quality_tier (bronze | silver | …) to numeric QUALITY_LEVELS for badges.
 * @param {string} [tier]
 * @returns {number|null}
 */
export const qualityTierStringToLevel = (tier) => {
  if (tier == null || tier === '') return null;
  const s = String(tier).toLowerCase();
  if (s === 'bronze') return QUALITY_LEVELS.SPEND;
  if (s === 'silver') return QUALITY_LEVELS.ACTIVITY;
  if (s === 'gold') return QUALITY_LEVELS.METERED;
  if (s === 'estimated') return QUALITY_LEVELS.ESTIMATED;
  return null;
};

/**
 * Maps numeric quality level (1-4) to a tier name.
 * @param {number} level
 * @returns {string}
 */
export const qualityLevelToTier = (level) => QUALITY_TIERS[level]?.name || 'Unknown';

/** Reads a quality level already carried by a record, numeric field first then tier string. */
const readRecordQualityLevel = (record) => {
  const numeric = record?.qualityLevel ?? record?.quality_level;
  if (numeric != null && QUALITY_TIERS[numeric]) return Number(numeric);
  return qualityTierStringToLevel(record?.qualityTier ?? record?.quality_tier);
};

// `unit_normalization` only rescales a reported quantity (e.g. MWh to kWh), so it does not make the
// usage an estimate. Any other derivation rule means the quantity itself was inferred.
const hasInferredQuantity = (record) => {
  const conversions = record?.usage_derivation?.conversions;
  if (!Array.isArray(conversions)) return false;
  return conversions.some((conversion) => String(conversion?.rule_id) !== 'unit_normalization');
};

const isSpendUsage = (record, metric) =>
  !!metric?.isSpend ||
  !!record?.isSpend ||
  !!record?.fx ||
  !!record?.rawUsageCurrency ||
  /\bspend\b/i.test(record?.metricName || '');

/**
 * Derives the data quality tier of a usage record. Usage records carry no quality field of their
 * own, so the tier is inferred from how the quantity was obtained; a tier already present on the
 * record (numeric level or `quality_tier` string from an import match) always wins.
 * @param {object} record - usage row from `state.usageData` or the add/edit modal item
 * @param {object} [options]
 * @param {Object<string, object>} [options.metricsById] - `state.metric.list` keyed by string id
 * @returns {{ level: number, reason: string }|null} null when there is not enough data to judge
 * @example
 * getUsageQuality(row, { metricsById })
 * // => { level: 2, reason: 'Based on spend rather than measured activity.' }
 */
export const getUsageQuality = (record, { metricsById } = {}) => {
  if (!record) return null;

  const recordLevel = readRecordQualityLevel(record);
  if (recordLevel != null) return { level: recordLevel, reason: 'Recorded with this usage entry.' };

  const metric = metricsById?.[String(record.metric)];
  const hasQuantity = record.usage != null && record.usage !== '';
  if (!metric && !hasQuantity && !record.suppliersEmissionsFactor) return null;

  if (hasInferredQuantity(record)) {
    return { level: QUALITY_LEVELS.ESTIMATED, reason: 'Quantity was inferred from the source data.' };
  }
  if (record.suppliersEmissionsFactor) {
    return { level: QUALITY_LEVELS.METERED, reason: 'Uses a supplier-specific emission factor.' };
  }
  if (isSpendUsage(record, metric)) {
    return { level: QUALITY_LEVELS.SPEND, reason: 'Based on spend rather than measured activity.' };
  }
  return { level: QUALITY_LEVELS.ACTIVITY, reason: 'Based on a reported activity quantity.' };
};

/**
 * Summarises the quality of a set of usage records for a grouped row.
 * @param {Array<object>} records
 * @param {object} [options] - forwarded to {@link getUsageQuality}
 * @returns {{ level: number, reason: string }|null} lowest tier in the group
 */
export const getGroupUsageQuality = (records, options) => {
  const levels = (records || []).map((record) => getUsageQuality(record, options)?.level).filter(Boolean);
  if (!levels.length) return null;

  const level = Math.min(...levels);
  const mixed = new Set(levels).size > 1;
  return {
    level,
    reason: mixed
      ? `Lowest of ${levels.length} entries, which range from ${qualityLevelToTier(level)} to ${qualityLevelToTier(
          Math.max(...levels),
        )}.`
      : `All ${levels.length} entries are ${qualityLevelToTier(level)}.`,
  };
};

/**
 * Maps numeric quality level to a color class for badges.
 * @param {number} level
 * @returns {string}
 */
export const qualityLevelToColor = (level) => QUALITY_TIERS[level]?.color || 'text-muted-foreground';

/**
 * Returns the uncertainty percentage for a quality level.
 * @param {number} level
 * @returns {number} e.g. 0.4 for +/-40%
 */
export const getUncertainty = (level) => UNCERTAINTY_BY_TIER[level] ?? 0.5;

/**
 * Calculates the uncertainty range for an emission value.
 * @param {number} emissions - tCO2e
 * @param {number} qualityLevel
 * @returns {{ low: number, high: number, pct: number }}
 */
export const getUncertaintyRange = (emissions, qualityLevel) => {
  const pct = getUncertainty(qualityLevel);
  return {
    low: Math.round(emissions * (1 - pct) * 10) / 10,
    high: Math.round(emissions * (1 + pct) * 10) / 10,
    pct: Math.round(pct * 100),
  };
};

/**
 * Computes materiality-weighted quality score from per-category data.
 * Formula: sum(category_emissions / total_emissions * category_quality_level)
 * @param {Array<{ emissions: number, qualityLevel: number }>} categories
 * @returns {number} weighted score 1-4
 */
export const computeMaterialityWeightedScore = (categories) => {
  const totalEmissions = categories.reduce((sum, c) => sum + (c.emissions || 0), 0);
  if (totalEmissions === 0) return 0;

  return categories.reduce((score, c) => {
    const weight = (c.emissions || 0) / totalEmissions;
    return score + weight * (c.qualityLevel || 1);
  }, 0);
};

/**
 * Determines the overall tier from a weighted score.
 * @param {number} score
 * @returns {string}
 */
export const scoreToTier = (score) => {
  if (score >= 3.5) return QUALITY_TIER_NAMES.GOLD;
  if (score >= 2.5) return QUALITY_TIER_NAMES.SILVER;
  if (score >= 1.5) return QUALITY_TIER_NAMES.BRONZE;
  return QUALITY_TIER_NAMES.ESTIMATED;
};

/**
 * Computes combined uncertainty using root-sum-of-squares for independent sources.
 * @param {Array<{ emissions: number, qualityLevel: number }>} categories
 * @returns {{ combined: number, low: number, high: number, total: number }}
 */
export const computeCombinedUncertainty = (categories) => {
  const totalEmissions = categories.reduce((sum, c) => sum + (c.emissions || 0), 0);
  if (totalEmissions === 0) return { combined: 0, low: 0, high: 0, total: 0 };

  const sumOfSquares = categories.reduce((sum, c) => {
    const u = getUncertainty(c.qualityLevel);
    const abs = (c.emissions || 0) * u;
    return sum + abs * abs;
  }, 0);

  const absoluteUncertainty = Math.sqrt(sumOfSquares);
  const combined = absoluteUncertainty / totalEmissions;

  return {
    combined: Math.round(combined * 100),
    low: Math.round((totalEmissions - absoluteUncertainty) * 10) / 10,
    high: Math.round((totalEmissions + absoluteUncertainty) * 10) / 10,
    total: Math.round(totalEmissions * 10) / 10,
  };
};

/**
 * Identifies high-impact/low-quality categories for upgrade prompts.
 * @param {Array<{ category: string, emissions: number, qualityLevel: number }>} categories
 * @param {number} [materialityThreshold=0.1] - min % of total to be considered material
 * @returns {Array}
 */
export const getUpgradeOpportunities = (categories, materialityThreshold = 0.1) => {
  const totalEmissions = categories.reduce((sum, c) => sum + (c.emissions || 0), 0);
  if (totalEmissions === 0) return [];

  return categories
    .filter((c) => {
      const pct = (c.emissions || 0) / totalEmissions;
      return pct >= materialityThreshold && (c.qualityLevel || 1) < QUALITY_LEVELS.METERED;
    })
    .sort((a, b) => (b.emissions || 0) - (a.emissions || 0))
    .map((c) => {
      const targetLevel = Math.min((c.qualityLevel || 1) + 1, QUALITY_LEVELS.METERED);
      return {
        category: c.category,
        currentTier: qualityLevelToTier(c.qualityLevel),
        targetTier: qualityLevelToTier(targetLevel),
        emissions: c.emissions,
        pctOfTotal: Math.round(((c.emissions || 0) / totalEmissions) * 100),
      };
    });
};
